import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import moment from 'moment';
import ReactSelect from 'react-select';
import AsyncSelect from 'react-select/async';

import { popupData, PopupNames } from 'core';
import { usePopupStateOperations, useInvoiceMatching } from 'core/hooks';
import {
  PaginatedStudentsDocument,
  PaginatedStudentsQuery,
  PaginatedStudentsQueryVariables,
  StudentAgreementsForLinkingDocument,
  StudentAgreementsForLinkingQuery,
  StudentAgreementsForLinkingQueryVariables,
} from 'core/graphql';
import { Button } from 'atoms';
import { Colors } from 'services/ColorService';

import { PopupContainer } from '../PopupContainer';

/**
 * Phase 13: two-mode popup that drives the canonical Invoice ↔ Payment
 * linkage flow.
 *
 *  - `mode='link'`   — pick a student → agreement → existing Payment.
 *                       Calls `linkFakturowniaInvoiceToPayment`.
 *  - `mode='create'` — pick a student → agreement (+ optional date).
 *                       Calls `createPaymentForFakturowniaInvoice`, which
 *                       provisions the Payment on that agreement and pins
 *                       it 1:1 to the invoice in one mutation.
 *
 * Replaces the legacy `MatchInvoicePopup` for any new code path. The old
 * popup stays mounted (`PopupNames.matchInvoice`) for any rare caller that
 * still wants the deprecated `matchFakturowniaInvoice` mutation.
 */
export interface ILinkInvoicePaymentPopupData {
  invoiceId: string;
  mode: 'link' | 'create';
  /** Buyer email from the invoice — prefilled as the student search query. */
  buyerEmail?: string | null;
  /** When the auto-matcher already guessed a student via email, preselect it. */
  prefillStudentId?: string | null;
  /** Run after a successful link/create — the host page should refetch. */
  onLinked?: () => void;
}

interface ISelectOption {
  value: string;
  label: string;
}

interface IAgreementOption extends ISelectOption {
  currency?: string | null;
  groupName?: string | null;
}

interface IPaymentOption extends ISelectOption {
  agreementId: string;
}

export const LinkInvoicePaymentPopup = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();
  const data = popupData()[PopupNames.linkInvoicePayment];

  const apollo = useApolloClient();
  const { linkToPayment, createPayment, loading } = useInvoiceMatching();

  const [student, setStudent] = useState<ISelectOption | null>(null);
  const [agreement, setAgreement] = useState<IAgreementOption | null>(null);
  const [payment, setPayment] = useState<IPaymentOption | null>(null);
  const [paymentDate, setPaymentDate] = useState<string>('');

  const [agreementOpts, setAgreementOpts] = useState<IAgreementOption[]>([]);
  const [paymentOpts, setPaymentOpts] = useState<IPaymentOption[]>([]);
  const [loadingAgreements, setLoadingAgreements] = useState(false);
  const [agreementsError, setAgreementsError] = useState<string | null>(null);

  const close = useCallback(() => {
    setStudent(null);
    setAgreement(null);
    setPayment(null);
    setPaymentDate('');
    setAgreementOpts([]);
    setPaymentOpts([]);
    setAgreementsError(null);
    hidePopup(PopupNames.linkInvoicePayment);
  }, [hidePopup]);

  // Pull the agreements + their payments of the chosen student. Errors are
  // caught explicitly so a backend hiccup doesn't bubble up as an unhandled
  // rejection — we render a soft notice instead.
  useEffect(() => {
    if (!student) {
      setAgreementOpts([]);
      setPaymentOpts([]);
      setAgreement(null);
      setPayment(null);
      setAgreementsError(null);
      return;
    }
    let cancelled = false;
    setLoadingAgreements(true);
    setAgreementsError(null);
    apollo
      .query<
        StudentAgreementsForLinkingQuery,
        StudentAgreementsForLinkingQueryVariables
      >({
        query: StudentAgreementsForLinkingDocument,
        variables: { studentId: student.value },
        fetchPolicy: 'no-cache',
      })
      .then((result) => {
        if (cancelled) return;
        const agreements = result.data?.student?.agreements || [];
        const aOpts: IAgreementOption[] = [];
        const pOpts: IPaymentOption[] = [];
        agreements.forEach((a) => {
          if (!a || a.terminated) return;
          const groupName = a.group?.name || '';
          aOpts.push({
            value: a.id,
            label: groupName
              ? `${a.agreementTitle} · ${groupName}`
              : a.agreementTitle,
            currency: a.currency,
            groupName,
          });
          (a.payments || []).forEach((p) => {
            if (!p) return;
            const dateLabel = p.date
              ? moment(p.date).format('DD.MM.YYYY')
              : '—';
            const amountLabel = `${p.amountAgreement ?? '—'} ${
              p.currency ?? ''
            }`.trim();
            pOpts.push({
              value: p.id,
              agreementId: a.id,
              label: `${dateLabel} · ${amountLabel} · ${a.agreementTitle}`,
            });
          });
        });
        setAgreementOpts(aOpts);
        setPaymentOpts(pOpts);
        // Auto-pick when there's only one active agreement — the most
        // common case and one fewer click for finance staff.
        if (aOpts.length === 1) setAgreement(aOpts[0]);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setAgreementOpts([]);
        setPaymentOpts([]);
        setAgreementsError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingAgreements(false);
      });
    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [student, apollo]);

  // When the agreement changes, narrow the payment options to that one.
  const visiblePaymentOpts = useMemo(
    () =>
      agreement
        ? paymentOpts.filter((p) => p.agreementId === agreement.value)
        : paymentOpts,
    [paymentOpts, agreement],
  );

  // Async student search — same shape MatchInvoicePopup uses, with the
  // buyer email auto-prefilled so the popular «student already exists with
  // this email» path is one Enter away.
  const loadStudentOptions = useCallback(
    async (input: string): Promise<ISelectOption[]> => {
      try {
        const result = await apollo.query<
          PaginatedStudentsQuery,
          PaginatedStudentsQueryVariables
        >({
          query: PaginatedStudentsDocument,
          variables: { query: input || data?.buyerEmail || '', first: 20 },
          fetchPolicy: 'no-cache',
        });
        const edges = result.data?.allStudents?.edges || [];
        const opts: ISelectOption[] = [];
        edges.forEach((edge) => {
          const node = edge?.node;
          if (!node || !node.user) return;
          const u = node.user;
          const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
          opts.push({
            value: node.id,
            label: `${fullName || '—'} · ${u.email || ''}`,
          });
        });
        return opts;
      } catch {
        return [];
      }
    },
    [apollo, data?.buyerEmail],
  );

  const canSubmit = useMemo(() => {
    if (!data || !student || !agreement) return false;
    if (data.mode === 'link' && !payment) return false;
    return true;
  }, [data, student, agreement, payment]);

  const onSubmit = useCallback(async () => {
    if (!data || !agreement) return;
    let ok = false;
    if (data.mode === 'link' && payment) {
      ok = await linkToPayment(data.invoiceId, payment.value);
    } else if (data.mode === 'create') {
      ok = await createPayment(
        data.invoiceId,
        agreement.value,
        paymentDate || undefined,
      );
    }
    if (ok) {
      data.onLinked?.();
      close();
    }
  }, [
    data,
    agreement,
    payment,
    paymentDate,
    linkToPayment,
    createPayment,
    close,
  ]);

  if (!data) {
    return null;
  }

  const title =
    data.mode === 'create'
      ? t('pages.fakturownia.linkPaymentPopup.titleCreate')
      : t('pages.fakturownia.linkPaymentPopup.titleLink');

  return (
    <PopupContainer width="600px" onClose={close} title={title}>
      <Body>
        {data.buyerEmail ? (
          <Hint>
            {t('pages.fakturownia.matchPopup.hint', { email: data.buyerEmail })}
          </Hint>
        ) : null}

        <Field>
          <Label>{t('pages.fakturownia.linkPaymentPopup.studentLabel')}</Label>
          <AsyncSelect
            autoFocus
            cacheOptions
            defaultOptions
            value={student}
            onChange={(v) => setStudent((v as ISelectOption) || null)}
            loadOptions={loadStudentOptions}
            placeholder={t('pages.fakturownia.matchPopup.studentPlaceholder')}
          />
        </Field>

        {student ? (
          <Field>
            <Label>
              {t('pages.fakturownia.linkPaymentPopup.agreementLabel')}
            </Label>
            <ReactSelect
              value={agreement}
              isLoading={loadingAgreements}
              options={agreementOpts}
              onChange={(v) => {
                setAgreement((v as IAgreementOption) || null);
                setPayment(null);
              }}
              placeholder={t(
                'pages.fakturownia.linkPaymentPopup.noActiveAgreements',
              )}
            />
            {agreementsError ? (
              <ErrorNotice>{agreementsError}</ErrorNotice>
            ) : null}
          </Field>
        ) : null}

        {data.mode === 'link' && agreement ? (
          <Field>
            <Label>
              {t('pages.fakturownia.linkPaymentPopup.paymentLabel')}
            </Label>
            <ReactSelect
              value={payment}
              options={visiblePaymentOpts}
              onChange={(v) => setPayment((v as IPaymentOption) || null)}
              placeholder={t(
                'pages.fakturownia.linkPaymentPopup.paymentPlaceholder',
              )}
              noOptionsMessage={() =>
                t('pages.fakturownia.linkPaymentPopup.noPaymentsOnAgreement')
              }
            />
          </Field>
        ) : null}

        {data.mode === 'create' && agreement ? (
          <Field>
            <Label>
              {t('pages.fakturownia.linkPaymentPopup.paymentDateLabel')}
            </Label>
            <TextInput
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </Field>
        ) : null}

        <Actions>
          <Button
            onClick={close}
            variant="outline"
            colors={{ backgroundColor: Colors.White, color: Colors.Blue }}
          >
            {t('pages.fakturownia.matchPopup.cancel')}
          </Button>
          <Button
            onClick={onSubmit}
            variant="primary"
            disabled={!canSubmit || loading}
            loading={loading}
            colors={{ backgroundColor: Colors.Blue, color: Colors.White }}
          >
            {data.mode === 'create'
              ? t('pages.fakturownia.linkPaymentPopup.submitCreate')
              : t('pages.fakturownia.linkPaymentPopup.submitLink')}
          </Button>
        </Actions>
      </Body>
    </PopupContainer>
  );
});

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  border-radius: 8px;
`;

const ErrorNotice = styled.div`
  font-size: 12px;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 8px 10px;
  border-radius: 6px;
`;

const TextInput = styled.input`
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #2563eb;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`;
